/**
 * KAN-757: ячейка таблицы CRM с правкой на месте. Клик по значению (и по
 * прочерку пустого) открывает редактор. Enter или уход фокуса сохраняет,
 * Esc отменяет. Многострочный редактор сохраняет по Ctrl+Enter (Cmd+Enter)
 * или уходу фокуса, Enter переносит строку. Список сохраняет сразу при выборе.
 * Без изменений запрос не отправляется. Текст ошибки сервера виден под ячейкой
 * до следующей правки.
 * Дата (datetime): уход фокуса с пустым полем ничего не стирает (неполный ввод
 * в datetime-local тоже даёт ""), очистка только кнопкой "Очистить" или Enter
 * на пустом поле. Неполная дата (validity.badInput) не сохраняется.
 */
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

const BAD_DATE_TEXT = 'Неполная дата';

export type CrmEditorKind = 'text' | 'textarea' | 'datetime' | 'select';

export interface CrmEditorOption {
  value: string;
  label: string;
}

interface CrmEditableCellProps {
  /** Название поля: подпись редактора и кнопки "Изменить: ...". */
  label: string;
  /** Значение для редактора ('' если пусто). */
  value: string;
  kind?: CrmEditorKind;
  options?: readonly CrmEditorOption[];
  /** Сохранить; результат: текст ошибки или null. */
  onSave: (value: string) => Promise<string | null>;
  /** Показ значения вне редактора. */
  children: ReactNode;
  /** Рядом со значением (иконка-ссылка), вне кнопки правки. */
  addon?: ReactNode;
  /** KAN-864: перед значением, вне кнопки правки (аватар клиента: сам кнопка). */
  leading?: ReactNode;
}

const FIELD = 'w-full rounded border border-blue-400 bg-white px-1.5 py-0.5 text-sm outline-none focus:ring-2 focus:ring-blue-200';

export function CrmEditableCell({
  label,
  value,
  kind = 'text',
  options = [],
  onSave,
  children,
  addon,
  leading,
}: CrmEditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  // Закрытие редактора один раз: Enter/Esc, затем blur при размонтировании.
  const closedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const open = () => {
    closedRef.current = false;
    setDraft(value);
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    closedRef.current = true;
    setEditing(false);
  };

  const finish = (next: string) => {
    closedRef.current = true;
    setEditing(false);
    if (next === value) return;
    void onSave(next).then(setError);
  };

  /** Сохранить черновик; via: Enter или уход фокуса. */
  const commit = (raw: string, via: 'enter' | 'blur') => {
    if (closedRef.current) return;
    if (kind === 'datetime') {
      if (inputRef.current?.validity.badInput) {
        setError(BAD_DATE_TEXT);
        if (via === 'blur') cancel();
        return;
      }
      if (raw === '' && via === 'blur') {
        cancel();
        return;
      }
    }
    finish(kind === 'datetime' || kind === 'select' ? raw : raw.trim());
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
      return;
    }
    if (event.key !== 'Enter') return;
    if (kind === 'textarea' && !(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    commit(draft, 'enter');
  };

  // KAN-864: the leading avatar stays in place while editing (the cell does not shift).
  const row = leading ? 'flex items-center gap-1' : 'flex items-start gap-1';
  if (editing) {
    if (!leading) return renderEditor();
    return (
      <div className={row}>
        {leading}
        <div className="min-w-0 flex-1">{renderEditor()}</div>
      </div>
    );
  }

  return (
    <div>
      <div className={row}>
        {leading}
        <button
          type="button"
          aria-label={`Изменить: ${label}`}
          title="Нажмите, чтобы изменить"
          onClick={open}
          className="-mx-1 min-w-0 flex-1 cursor-text rounded px-1 text-left hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
        >
          {children}
        </button>
        {addon}
      </div>
      {error && (
        <p role="alert" className="mt-0.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );

  function renderEditor(): JSX.Element {
    const common = {
      'aria-label': label,
      autoFocus: true,
      onKeyDown,
      onBlur: () => commit(draft, 'blur'),
    };
    if (kind === 'textarea') {
      return <textarea {...common} rows={3} className={`${FIELD} min-w-[220px] resize-y`} value={draft} onChange={(e) => setDraft(e.target.value)} />;
    }
    if (kind === 'select') {
      return (
        <select {...common} className={FIELD} value={draft} onChange={(e) => commit(e.target.value, 'enter')}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }
    if (kind === 'datetime') {
      return (
        <div>
          <div className="flex items-center gap-1">
            <input
              {...common}
              ref={inputRef}
              type="datetime-local"
              className={FIELD}
              value={draft}
              onChange={(e) => {
                setError(null);
                setDraft(e.target.value);
              }}
            />
            <button
              type="button"
              className="shrink-0 rounded px-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-red-600"
              // Фокус остаётся в поле: иначе blur закрыл бы редактор до клика.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => finish('')}
            >
              Очистить
            </button>
          </div>
          {error && (
            <p role="alert" className="mt-0.5 text-xs text-red-600">
              {error}
            </p>
          )}
        </div>
      );
    }
    return <input {...common} type="text" className={FIELD} value={draft} onChange={(e) => setDraft(e.target.value)} />;
  }
}
